import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import "./VerifyCodePage.css";
import happyDog from "../../../assets/dogLevel2.png";
import apiClient from "../../../api/apiClient";
import { useGlobalAlert } from "../../../context/AlertContext";
import socialPawsLogo from "../../../assets/appIcon.svg";
import MobileTopBar from "../../../components/MobileTopBar";
import DesktopTopBar from "../../../components/DesktopTopBar";

const VerifyCodePage = ({ formDataEmail, setFormDataEmail }) => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isResent, setIsResent] = useState(false);

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  const isCodeComplete = code.every((digit) => digit !== "");

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleResendCode = async () => {
    if (isResending) return;
    setIsResending(true);

    try {
      const response = await apiClient.post("/user/send-reset-password-otp", {
        user_email: formDataEmail.email,
      });

      if (response.data.status) {
        setIsResent(true);
        triggerAlert("Verification code resent successfully!", "success");
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        triggerAlert(
          response.data.message || "Failed to resend code.",
          "error",
        );
      }
    } catch (error) {
      console.error("Resend API Failed", error);
      triggerAlert("Something went wrong. Please try again.", "error");
    } finally {
      setIsResending(false);
    }
  };

  const handleChange = (index, e) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newCode = [...code];
    newCode[index] = value.substring(value.length - 1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputRefs.current[index - 1].focus();
      } else {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.split("").slice(0, 6);
    const newCode = [...code];
    digits.forEach((digit, i) => {
      newCode[i] = digit;
    });
    setCode(newCode);

    const lastFilledIndex = Math.min(digits.length - 1, 5);
    inputRefs.current[lastFilledIndex].focus();
  };

  const handleContinue = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6 || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await apiClient.post("/user/verify-reset-password-otp", {
        user_email: formDataEmail.email,
        otp_code: fullCode,
      });

      if (response.data.status) {
        navigate("/create-password", {
          state: {
            email: formDataEmail.email,
            otp: fullCode,
          },
        });
      } else {
        triggerAlert(response.data.message || "Invalid code.", "error");
      }
    } catch (error) {
      console.error("Verification Failed", error);
      const errorMessage =
        error.response?.data?.message ||
        "Something went wrong during verification.";
      triggerAlert(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vc-page-container">
      {/* Desktop Header (Hidden on Mobile) */}
      {/* <div className="fp-header fp-desktop-only">
        <button className="fp-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} color="#fff" strokeWidth={3} />
          <span>Back</span>
        </button>
      </div> */}
      <DesktopTopBar
        showBack={true}
        onBack={() => navigate(-1)}
        backColor="#fff"
        showProgress={false}
      />

      {/* Mobile Header (Hidden on Desktop) */}
      {/* <div className="fp-mobile-header fp-mobile-only">
        <button
          className="fp-back-btn fp-mobile-back-override"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={22} color="#fff" strokeWidth={3} />
          <span>Back</span>
        </button>
        <div className="fp-header-logo">
          <img src={socialPawsLogo} alt="Social Paws" className="fp-logo-img" />
          <span>Social Paws</span>
        </div>
      </div> */}
      <MobileTopBar
        onBack={() => navigate(-1)}
        backColor="#fff"
        logoColor="#fff"
        isProtectedRoute={false}
      />

      <div className="vc-content-wrapper">
        <div className="vc-mascot-container">
          <img src={happyDog} alt="Happy Dog" />
        </div>

        <h1 className="vc-title">Verify Code</h1>

        <p className="vc-subtitle">
          We have sent a verification code to{" "}
          <span className="vc-email-link">{formDataEmail.email}</span>
        </p>

        <div className="vc-form-area">
          <label className="vc-input-label">Enter the 6-digit code</label>

          <div className="vc-otp-container">
            {code.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                className={`vc-otp-input ${digit ? "filled" : ""}`}
                value={digit}
                ref={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => handleChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                inputMode="numeric"
                disabled={isSubmitting}
              />
            ))}
          </div>

          <div className="vc-resend-container">
            <button
              onClick={handleResendCode}
              disabled={isResending || isSubmitting || isResent}
              className="vc-resend-btn"
              style={{
                cursor:
                  isResending || isSubmitting || isResent
                    ? "not-allowed"
                    : "pointer",
                opacity: isResending ? 0.7 : 1,
              }}
            >
              {isResending ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  RESENDING...
                </>
              ) : isResent ? (
                <>Code Resent!</>
              ) : (
                "Didn't receive a code? Resend Code"
              )}
            </button>
          </div>

          <button
            className={`vc-continue-btn ${!isCodeComplete || isSubmitting ? "disabled" : ""}`}
            onClick={handleContinue}
            disabled={!isCodeComplete || isSubmitting}
            style={{
              opacity: isCodeComplete && !isSubmitting ? 1 : 0.5,
              cursor:
                isCodeComplete && !isSubmitting ? "pointer" : "not-allowed",
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                VERIFYING...
              </>
            ) : (
              "CONTINUE"
            )}
          </button>

          <div className="vc-signup-link">
            Don't have an account?{" "}
            <span onClick={() => navigate("/onboarding")}>Sign Up Here</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCodePage;
